/**
 * useWorkflows — maps WorkflowDefinition[] to WorkflowUIItem[] for component consumption.
 *
 * React components never import from the registry directly.
 * Supports category, domain, and search filtering.
 */

import { useMemo } from "react";
import { WORKFLOW_REGISTRY } from "@flowstate/types/workflow-registry";
import type { WorkflowUIItem, WorkflowCategory } from "@flowstate/types";

export function useWorkflows(opts?: {
  category?: WorkflowCategory;
  domain?: string;
  search?: string;
}) {
  const { category, domain, search } = opts ?? {};

  const workflows = useMemo((): WorkflowUIItem[] => {
    return WORKFLOW_REGISTRY
      .filter((def) => {
        if (category && def.category !== category) return false;
        if (domain && def.domain !== domain) return false;
        if (search) {
          const q = search.toLowerCase();
          return (
            def.name.toLowerCase().includes(q) ||
            def.description.toLowerCase().includes(q) ||
            def.domain.toLowerCase().includes(q)
          );
        }
        return true;
      })
      .map((def) => ({
        id: def.id,
        name: def.name,
        description: def.description,
        category: def.category,
        domain: def.domain,
        iconKey: def.iconKey,
        stepCount: def.steps.length,
        version: def.version,
        preflight: def.preflight,
      }));
  }, [category, domain, search]);

  const availableDomains = useMemo(
    () => [...new Set(WORKFLOW_REGISTRY.map((wf) => wf.domain))].sort(),
    []
  );

  return { workflows, availableDomains };
}
